// --- React & Redux
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
// --- UI Libraries
import DataGrid from '@dtci/spellbook/DataGrid';
// --- Styles
import styles from 'datastage/components/AGGrid/details-table.module.scss';
// --- Custom Hooks & Utils
import { useAgGridApi } from 'datastage/components/AGGrid/hooks';
import { useUsersActions } from 'datastage/store/users/useUsers';
import { useUserGridUtils } from './utils/gridUtils';

/**
 * Users Grid Component
 *
 * Features:
 * - Displays a list of users in a data grid
 * - Handles row click to open the edit user drawer
 * - Integrates with Redux for user state and filters
 * - Uses custom hooks for grid API and loading state
 * - Supports sortable columns with custom headers
 *
 * @returns {JSX.Element}
 */
export const UsersGrid = () => {
    const agGridApi = useAgGridApi();
    const { users } = useSelector((s) => s.users);
    const { updateUsers } = useUsersActions();

    const { columns } = useUserGridUtils();
    const showSpinner = useSelector((s) => s.spinner.showSpinner);

    /**
     * Handle row click: open edit drawer with selected user.
     * Uses the unified drawerMode pattern.
     */
    const onRowClicked = useCallback(
        (event) => {
            updateUsers({
                drawerMode: 'edit',
                selectedUser: event.data,
            });
        },
        [updateUsers],
    );

    return (
        <>
            <DataGrid
                className={styles.dataGrid}
                autoSizing
                agGrid={{
                    ref: agGridApi.AgGridApiRef,
                    rowData: users,
                    headerHeight: 45,
                    onRowClicked: onRowClicked,
                    defaultColDef: {
                        wrapText: true,
                        autoHeight: true,
                        suppressMenu: true,
                        resizable: true,
                        cellStyle: { 
                            paddingTop: '10px', 
                            paddingBottom: '10px',
                            wordBreak: 'normal',
                            overflowWrap: 'break-word',
                        },
                    },
                }}
                bordered="none"
                columnDefs={columns}
                sortable={false}
                striped
                noResultsMessage={showSpinner ? 'Loading...' : 'No Users Found'}
            />
        </>
    );
};
